import { useQuery } from "@apollo/client";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { DataGrid, CustomGridColDef } from "components/DataGrid";
import { gql } from "generated-graphql";
import {
  ScoutNodeConversationsQuery,
  ScoutNodeConversationsQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintDateTimeWithSeconds } from "util/dates";

const SCOUT_NODE_CONVERSATIONS_QUERY = gql(`
  query ScoutNodeConversations($nodeId: String!) {
    scoutNodeConversations(nodeId: $nodeId) {
      conversationId
      externalId
      title
      status
      turnCount
      lastTurnAt
      leaseExpiresAt
    }
  }
`);

type ScoutNodeConversationRow = ScoutNodeConversationsQuery["scoutNodeConversations"][number];

// Idle-affinity lease length; used to color staleness as a conversation
// approaches eviction. Kept as a display constant — the source of truth is
// SCOUT_CONFIG.timing.conversationIdleLockTtlMs on the backend.
const LEASE_MS = 4 * 60 * 1000;

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) {
    return "";
  }
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
}

// Staleness color: green while fresh, neutral mid-lease, amber as the
// conversation nears the end of its idle-affinity lease (eviction).
function stalenessColor(ms: number): string {
  if (ms < 60_000) {
    return "success.main";
  }
  if (ms < LEASE_MS * 0.8) {
    return "text.primary";
  }
  return "warning.main";
}

export function ScoutNodeDetail() {
  const { nodeId } = useParams<{ nodeId: string }>();
  useBreadcrumb({ label: nodeId ? `Node ${nodeId}` : "Scout Node" });

  // Poll so staleness/lease stay live while the node is in use.
  const { data, loading } = useQuery<
    ScoutNodeConversationsQuery,
    ScoutNodeConversationsQueryVariables
  >(SCOUT_NODE_CONVERSATIONS_QUERY, {
    variables: { nodeId: nodeId ?? "" },
    skip: !nodeId,
    fetchPolicy: "cache-and-network",
    pollInterval: 10_000,
  });

  const rows = useMemo(() => data?.scoutNodeConversations ?? [], [data]);
  // Single clock per render so staleness/lease columns stay mutually consistent.
  const nowMs = Date.now();

  const columns: CustomGridColDef<ScoutNodeConversationRow>[] = useMemo(
    () => [
      {
        field: "staleness",
        headerName: "Stale for",
        flex: 0.6,
        minWidth: 120,
        valueGetter(_, row) {
          return row.lastTurnAt ? nowMs - new Date(row.lastTurnAt).getTime() : null;
        },
        renderCell({ value }) {
          if (typeof value !== "number") {
            return (
              <Typography variant="body2" color="text.secondary">
                no turns
              </Typography>
            );
          }
          return (
            <Typography variant="body2" sx={{ color: stalenessColor(value) }}>
              {formatDuration(value)}
            </Typography>
          );
        },
      },
      {
        field: "leaseRemaining",
        headerName: "Lease left",
        flex: 0.5,
        minWidth: 110,
        valueGetter(_, row) {
          return new Date(row.leaseExpiresAt).getTime() - nowMs;
        },
        renderCell({ value }) {
          if (typeof value !== "number") {
            return "";
          }
          return formatDuration(value);
        },
      },
      {
        field: "turnCount",
        headerName: "Turns",
        flex: 0.3,
        minWidth: 80,
        type: "number",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        minWidth: 120,
        renderCell({ value }) {
          return value ? <Chip size="small" label={value} /> : "";
        },
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.2,
        minWidth: 220,
        renderCell({ value }) {
          return (
            value ?? (
              <Typography variant="body2" color="text.secondary">
                (untitled)
              </Typography>
            )
          );
        },
      },
      {
        field: "lastTurnAt",
        headerName: "Last turn",
        flex: 0.8,
        minWidth: 190,
        renderCell({ value }) {
          return value ? prettyPrintDateTimeWithSeconds(value) : "";
        },
      },
      {
        field: "externalId",
        headerName: "External ID",
        flex: 1,
        minWidth: 200,
        linkable: false,
      },
      {
        field: "conversationId",
        headerName: "Conversation ID",
        flex: 1,
        minWidth: 220,
        linkable: false,
      },
    ],
    [nowMs]
  );

  return (
    <Stack gap={2} sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="baseline" gap={1.5} flexWrap="wrap">
        <Typography variant="h5">Scout node</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontFamily: "monospace" }}>
          {nodeId}
        </Typography>
        <Chip
          size="small"
          label={`${rows.length} held conversation${rows.length === 1 ? "" : "s"}`}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Conversations currently pinned to this node by an active lock — in-flight turns plus
        recently-active conversations kept warm on their idle-affinity lease (~4 min). Click a row
        to open the conversation. Refreshes every 10s.
      </Typography>
      <Box sx={{ height: "70vh", minHeight: 400 }}>
        <DataGrid<ScoutNodeConversationRow>
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.conversationId}
          getRowLink={(row) => `/staff/observe/scout-conversation/${row.conversationId}`}
          noRowsMessage="This node holds no active conversation locks right now."
          initialState={{
            sorting: { sortModel: [{ field: "staleness", sort: "desc" }] },
          }}
        />
      </Box>
    </Stack>
  );
}
